import { createJsonMemoryAccessors } from '@/services/storage'

import type {
  AutomationFilters,
  AutomationJob,
  AutomationLog,
  AutomationLogLevel,
  AutomationSession,
  AutomationTask,
} from '../types'

type Slot = 'jobs' | 'sessions' | 'logs'

const memory: Record<Slot, unknown> = {
  jobs: null,
  sessions: null,
  logs: null,
}

const KEYS = {
  jobs: 'masterhub.automation.jobs.v1',
  sessions: 'masterhub.automation.sessions.v1',
  logs: 'masterhub.automation.logs.v1',
} as const

const { readJson, writeJson } = createJsonMemoryAccessors(memory)

export function resetAutomationPersistence(): void {
  memory.jobs = null
  memory.sessions = null
  memory.logs = null
}

export class AutomationJobRepository {
  list(filters: AutomationFilters = {}): AutomationJob[] {
    return readJson<AutomationJob[]>(KEYS.jobs, 'jobs', []).filter((job) => {
      if (filters.supplierId && job.supplierId !== filters.supplierId) return false
      if (filters.status && filters.status !== 'ALL' && job.status !== filters.status) return false
      if (filters.periodFrom && job.createdAt < filters.periodFrom) return false
      if (filters.periodTo && job.createdAt > filters.periodTo) return false
      return true
    })
  }

  upsertFromTask(task: AutomationTask): AutomationJob {
    const job: AutomationJob = {
      id: task.id,
      supplierId: task.supplier,
      type: task.operation,
      status: task.status,
      priority: task.priority,
      attempts: task.attempts,
      startedAt: task.startedAt,
      finishedAt: task.finishedAt,
      executionTime: task.duration,
      errorMessage: task.error,
      createdAt: task.createdAt,
      updatedAt: new Date().toISOString(),
    }
    const list = readJson<AutomationJob[]>(KEYS.jobs, 'jobs', [])
    const index = list.findIndex((j) => j.id === job.id)
    if (index >= 0) list[index] = job
    else list.unshift(job)
    writeJson(KEYS.jobs, 'jobs', list.slice(0, 500))
    return job
  }

  reset(): void {
    writeJson(KEYS.jobs, 'jobs', [])
  }
}

export class AutomationSessionRepository {
  list(): AutomationSession[] {
    return readJson<AutomationSession[]>(KEYS.sessions, 'sessions', [])
  }

  upsert(session: AutomationSession): AutomationSession {
    const list = this.list()
    const index = list.findIndex((s) => s.id === session.id)
    if (index >= 0) list[index] = session
    else list.unshift(session)
    writeJson(KEYS.sessions, 'sessions', list.slice(0, 200))
    return session
  }

  reset(): void {
    writeJson(KEYS.sessions, 'sessions', [])
  }
}

export class AutomationLogRepository {
  list(jobId?: string): AutomationLog[] {
    const all = readJson<AutomationLog[]>(KEYS.logs, 'logs', [])
    if (!jobId) return all
    return all.filter((l) => l.jobId === jobId)
  }

  add(jobId: string, level: AutomationLogLevel, message: string): AutomationLog {
    const entry: AutomationLog = {
      id: crypto.randomUUID(),
      jobId,
      level,
      message,
      createdAt: new Date().toISOString(),
    }
    writeJson(KEYS.logs, 'logs', [entry, ...this.list()].slice(0, 1000))
    return entry
  }

  reset(): void {
    writeJson(KEYS.logs, 'logs', [])
  }
}

export const automationJobRepository = new AutomationJobRepository()
export const automationSessionRepository = new AutomationSessionRepository()
export const automationLogRepository = new AutomationLogRepository()
